import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingDown, TrendingUp, Minus } from 'lucide-react';

const fmt = (n) => '$' + Math.abs(n).toLocaleString();

const CATEGORIES = [
    { key: 'vehicleCost', label: 'Vehicle + Tax' },
    { key: 'financingCost', label: 'Financing' },
    { key: 'maintenance', label: 'Maintenance' },
    { key: 'tires', label: 'Tires' },
    { key: 'insurance', label: 'Insurance' },
    { key: 'registration', label: 'Registration' },
    { key: 'energy', label: 'Energy / Fuel' },
];

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
        <div className="bg-white border border-gray-100 rounded-xl shadow-lg px-4 py-3 text-sm">
            <div className="font-semibold text-gray-800 mb-1">{label}</div>
            {payload.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ background: p.fill }} />
                    <span className="text-gray-500">{p.name}:</span>
                    <span className="font-semibold">{fmt(p.value)}</span>
                </div>
            ))}
        </div>
    );
};

export default function TCOResults({ evTCO, dieselTCO, evMake, evModel, dieselMake, dieselModel }) {
    const diff = evTCO.total - dieselTCO.total;
    const evCheaper = diff < 0;
    const equal = diff === 0;

    const chartData = CATEGORIES.map(c => ({
        name: c.label,
        EV: evTCO[c.key],
        DieselGas: dieselTCO[c.key],
    }));

    return (
        <div className="space-y-5">
            {/* Top totals */}
            <div className="grid md:grid-cols-3 gap-4">
                <TotalCard label={`${evMake} ${evModel}`} value={evTCO.total} color="blue" />
                <DiffCard diff={diff} evCheaper={evCheaper} equal={equal} />
                <TotalCard label={`${dieselMake} ${dieselModel}`} value={dieselTCO.total} color="slate" />
            </div>

            {/* Chart */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h3 className="text-sm font-semibold text-gray-800 mb-5">Cost Breakdown by Category</h3>
                <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={chartData} barCategoryGap="30%" barGap={4}>
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                        <YAxis tickFormatter={v => '$' + (v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v)} tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="EV" name="Electric" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="DieselGas" name="Diesel/Gas" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-5 justify-center mt-2">
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-blue-500" /><span className="text-xs text-gray-500">Electric</span></div>
                    <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-slate-400" /><span className="text-xs text-gray-500">Diesel/Gas</span></div>
                </div>
            </div>

            {/* Breakdown table */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="grid grid-cols-3 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    <div className="px-6 py-3">Category</div>
                    <div className="px-4 py-3 text-right text-blue-500">Electric</div>
                    <div className="px-6 py-3 text-right text-slate-500">Diesel/Gas</div>
                </div>
                {CATEGORIES.map((c, i) => {
                    const evVal = evTCO[c.key];
                    const dVal = dieselTCO[c.key];
                    const delta = evVal - dVal;
                    return (
                        <div key={c.key} className={`grid grid-cols-3 items-center text-sm border-b border-gray-50 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                            <div className="px-6 py-3 text-gray-600">{c.label}</div>
                            <div className="px-4 py-3 text-right font-medium text-gray-800">
                                {fmt(evVal)}
                                {delta !== 0 && (
                                    <span className={`ml-1.5 text-xs ${delta < 0 ? 'text-green-500' : 'text-red-400'}`}>
                                        {delta < 0 ? '↓' : '↑'}{fmt(Math.abs(delta))}
                                    </span>
                                )}
                            </div>
                            <div className="px-6 py-3 text-right font-medium text-gray-800">{fmt(dVal)}</div>
                        </div>
                    );
                })}
                <div className="grid grid-cols-3 items-center bg-gray-900 text-white text-sm font-bold">
                    <div className="px-6 py-4">Total TCO</div>
                    <div className="px-4 py-4 text-right">{fmt(evTCO.total)}</div>
                    <div className="px-6 py-4 text-right">{fmt(dieselTCO.total)}</div>
                </div>
            </div>
        </div>
    );
}

function TotalCard({ label, value, color }) {
    return (
        <div className={`rounded-2xl border ${color === 'blue' ? 'border-blue-100 bg-blue-50' : 'border-gray-100 bg-gray-50'} px-6 py-5`}>
            <div className={`text-xs font-medium mb-1 ${color === 'blue' ? 'text-blue-400' : 'text-gray-400'}`}>{label}</div>
            <div className={`text-2xl font-bold ${color === 'blue' ? 'text-blue-700' : 'text-gray-800'}`}>${value.toLocaleString()}</div>
            <div className="text-xs text-gray-400 mt-0.5">Total Cost of Ownership</div>
        </div>
    );
}

function DiffCard({ diff, evCheaper, equal }) {
    const Icon = equal ? Minus : evCheaper ? TrendingDown : TrendingUp;
    const abs = Math.abs(diff);
    return (
        <div className={`rounded-2xl border px-6 py-5 flex flex-col items-center justify-center text-center
            ${equal ? 'border-gray-200 bg-white' : evCheaper ? 'border-green-100 bg-green-50' : 'border-amber-100 bg-amber-50'}`}>
            <Icon className={`w-5 h-5 mb-2 ${equal ? 'text-gray-400' : evCheaper ? 'text-green-500' : 'text-amber-500'}`} />
            <div className={`text-xl font-bold ${equal ? 'text-gray-600' : evCheaper ? 'text-green-600' : 'text-amber-600'}`}>
                {equal ? 'Equal' : `$${abs.toLocaleString()}`}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
                {equal ? 'Same total cost' : evCheaper ? 'EV saves over holding period' : 'EV costs more over holding period'}
            </div>
        </div>
    );
}
